import React, { useReducer, useEffect } from "react";
import { Cell } from "@components/table/types";
import { M_TABLE_HEADERS_LANDING_ZONES } from "../constants";
import { LandingZoneModel } from "@api/types/landingzones";
import { FilterType, Form, Table } from "@components";
import { getLandingZones } from "@api/admin/landingzones";
import { Box } from "@material-ui/core";
import { MaintenanceBreadcrumbs } from "../breadcrumbs";
import { LandingZoneDetail } from "./detail";
import { useLandingZoneStore } from "../../../../../store/hooksy/landingZones";
import { alertService } from "@services";

const headers: Cell[] = [
  { data: M_TABLE_HEADERS_LANDING_ZONES.NAME },
  { data: M_TABLE_HEADERS_LANDING_ZONES.IS_ACTIVE },
  { data: M_TABLE_HEADERS_LANDING_ZONES.APPROVER2 },
  { data: M_TABLE_HEADERS_LANDING_ZONES.APPROVER3 },
  { data: M_TABLE_HEADERS_LANDING_ZONES.APPROVER4 },
  { data: M_TABLE_HEADERS_LANDING_ZONES.APPROVER_DISTRO_2 },
  { data: M_TABLE_HEADERS_LANDING_ZONES.APPROVER_DISTRO_3 },
  { data: M_TABLE_HEADERS_LANDING_ZONES.APPROVER_DISTRO_4 },
];

type State = {
  landingZones: LandingZoneModel[];
  loading: boolean;
  selectedLandingZoneId: number;
  openModal: boolean;
};

type Action =
  | {
      type: "toggle-loading";
      value: State["loading"];
    }
  | {
      type: "toggle-modal";
      modalStatus: State["openModal"];
      selectedLandingZoneId?: number;
    }
  | {
      type: "fetch-success";
      value: State["landingZones"];
    };

function init(): State {
  return {
    landingZones: [],
    loading: false,
    selectedLandingZoneId: 0,
    openModal: false,
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "toggle-loading":
      return {
        ...state,
        loading: action.value === undefined ? !state.loading : action.value,
      };
    case "toggle-modal":
      return {
        ...state,
        openModal:
          action.modalStatus === undefined
            ? !state.openModal
            : action.modalStatus,
        selectedLandingZoneId: action.selectedLandingZoneId
          ? action.selectedLandingZoneId
          : 0,
      };
    case "fetch-success":
      return {
        ...state,
        loading: false,
        landingZones: action.value,
      };
    default:
      return state;
  }
}

export const LandingZones: React.FC = () => {
  const [localState, localDispatch] = useReducer(reducer, undefined, init);
  const [
    landingZoneHooksyStore,
    setLandingZoneHooksyStore,
  ] = useLandingZoneStore();

  // function fetchLandingZones() {
  //   localDispatch({
  //     type: "toggle-loading",
  //     value: true,
  //   });
  //   getLandingZones()
  //     .then((response) =>
  //       localDispatch({ type: "set-landing-zones", value: response })
  //     )
  //     .catch((err) => alertService.danger({ statusCode: err.response.status }))
  //     .finally(() => {
  //       localDispatch({ type: "toggle-loading", value: false });
  //       setLandingZoneHooksyStore({ fetchList: false });
  //     });
  // }

  function fetchLandingZones() {
    localDispatch({
      type: "toggle-loading",
      value: true,
    });
    getLandingZones()
      .then((response) =>
        localDispatch({ type: "fetch-success", value: response })
      )
      .catch((err) => {
        alertService.danger({ statusCode: err.response.status });
        localDispatch({ type: "toggle-loading", value: false });
      })
      .finally(() => {
        setLandingZoneHooksyStore({ fetchList: false });
      });
  }

  function getSelectedLandingZone(targetId: number): LandingZoneModel {
    return localState.landingZones.find(
      (lz) => lz.id === targetId
    ) as LandingZoneModel;
  }

  useEffect(() => {
    fetchLandingZones();
  }, []);

  useEffect(() => {
    if (landingZoneHooksyStore.fetchList) {
      fetchLandingZones();
    }
  }, [landingZoneHooksyStore]);

  useEffect(() => {
    if (localState.openModal) {
      if (localState.selectedLandingZoneId > 0) {
        const selectedLZ = getSelectedLandingZone(
          localState.selectedLandingZoneId
        );
        form.set(Form.target.VALUE, ["id", selectedLZ.id]);
        form.set(Form.target.VALUE, ["name", selectedLZ.name]);
        form.set(Form.target.VALUE, ["approver2", selectedLZ.approver2]);
        form.set(Form.target.VALUE, ["approver3", selectedLZ.approver3]);
        form.set(Form.target.VALUE, ["approver4", selectedLZ.approver4]);
        form.set(Form.target.VALUE, [
          "approverDistro2Id",
          selectedLZ.approverDistro2Id,
        ]);
        form.set(Form.target.VALUE, [
          "approverDistro3Id",
          selectedLZ.approverDistro3Id,
        ]);
        form.set(Form.target.VALUE, [
          "approverDistro4Id",
          selectedLZ.approverDistro4Id,
        ]);
        form.set(Form.target.VALUE, [
          "approverDistro2Name",
          selectedLZ.approverDistro2Name,
        ]);
        form.set(Form.target.VALUE, [
          "approverDistro3Name",
          selectedLZ.approverDistro3Name,
        ]);
        form.set(Form.target.VALUE, [
          "approverDistro4Name",
          selectedLZ.approverDistro4Name,
        ]);
        form.set(Form.target.VALUE, [
          "businessUnitId",
          selectedLZ.businessUnitId,
        ]);
        form.set(Form.target.VALUE, [
          "businessUnitName",
          selectedLZ.businessUnitName,
        ]);
        form.set(Form.target.VALUE, [
          "assignmentTeam",
          selectedLZ.assignmentTeam,
        ]);
        form.set(Form.target.VALUE, [
          "accessDetailNote",
          selectedLZ.accessDetailNote,
        ]);
        form.set(Form.target.VALUE, ["uamNote", selectedLZ.uamNote]);
        form.set(Form.target.VALUE, ["metadata", selectedLZ.metadata]);
        form.set(Form.target.VALUE, ["isActive", selectedLZ.isActive]);
      }
    }
  }, [localState.openModal]);

  const form = Form.useManager<LandingZoneModel>({
    id: 0,
    name: "",
    isActive: false,
    approver2: "",
    approver3: "",
    approver4: "",
    approverDistro2Id: 0,
    approverDistro2Name: "",
    approverDistro3Id: 0,
    approverDistro3Name: "",
    approverDistro4Id: 0,
    approverDistro4Name: "",
    businessUnitId: 0,
    businessUnitName: "",
    assignmentTeam: "",
    accessDetailNote: "",
    uamNote: "",
    metadata: "",
  });

  const isShowModal = localState.openModal ? (
    <LandingZoneDetail
      form={form}
      open={localState.openModal}
      onCancel={() =>
        localDispatch({ type: "toggle-modal", modalStatus: false })
      }
    />
  ) : null;

  const pageName = "GCP Landing Zones";
  return (
    <>
      <Box mt={2}>
        <MaintenanceBreadcrumbs title={pageName} />
        <Table
          data={localState.landingZones.map((lz: LandingZoneModel) => ({
            cells: [
              {
                data: lz.name,
                color: "blue",
                cursor: "pointer",
                onClick: () => {
                  // localDispatch({ type: "select-landing-zone", value: lz.id });
                  // localDispatch({ type: "toggle-modal", value: true });
                  localDispatch({
                    type: "toggle-modal",
                    modalStatus: true,
                    selectedLandingZoneId: lz.id,
                  });
                },
              },
              {
                data: lz.isActive ? "Yes" : "No",
              },
              {
                data: lz.approver2,
              },
              {
                data: lz.approver3,
              },
              {
                data: lz.approver4,
              },
              {
                data: lz.approverDistro2Id ? lz.approverDistro2Name : "",
              },
              {
                data: lz.approverDistro3Id ? lz.approverDistro3Name : "",
              },
              {
                data: lz.approverDistro4Id ? lz.approverDistro4Name : "",
              },
            ],
            data: localState.landingZones,
          }))}
          tableName={pageName}
          searchPlaceholder="Search by name"
          searchColumns={[M_TABLE_HEADERS_LANDING_ZONES.NAME]}
          headers={headers}
          filters={[FilterType.SORT, FilterType.SEARCH, FilterType.PAGINATOR]}
          toolbar="M_LANDING_ZONES_TOOLBAR"
          loading={localState.loading}
        />
        {isShowModal}
      </Box>
    </>
  );
};
